<template>
  <div class="league-page">
    <AppHeader mobile-header-mode="back-only" />

    <main class="league-content">
      <section v-if="isLoading" class="league-shell">
        <CardRailSkeleton />
      </section>

      <section v-else-if="leagueHub" class="league-shell">
        <header class="league-hero">
          <div class="hero-copy">
            <p class="sport-label">Football League Hub</p>
            <h1>{{ displayLeagueName }}</h1>
            <p class="hero-description">
              {{ leagueHub.league.season || 'Current season' }} | {{ leagueHub.league.upcoming_matches }} upcoming matches
            </p>
          </div>
        </header>

        <section class="league-intro">
          <p>
            Follow {{ displayLeagueName }} predictions, upcoming fixtures, and the current standings in one place. This
            page is built to help you move quickly from league-level context into the most relevant match pages.
          </p>
          <div class="league-intro-links">
            <router-link to="/football">All Football Predictions</router-link>
            <router-link to="/leagues-we-cover">Leagues We Cover</router-link>
            <router-link to="/methodology">How The Model Works</router-link>
          </div>
        </section>

        <section class="league-section">
          <div class="section-heading">
            <h2>Featured Matches</h2>
            <p>The most important upcoming fixtures to open first.</p>
          </div>

          <div class="featured-grid">
            <article
              v-for="game in featuredGames"
              :key="game.id"
              class="featured-card"
              @click="openGame(game.id)"
            >
              <div class="featured-top">
                <span class="featured-tag">{{ kickoffLabel(game.date) }}</span>
              </div>

              <div class="featured-matchup">
                <div class="team-stack">
                  <TeamLogoBadge :src="game.first_team_logo" :team-name="game.first_team" :size="46" />
                  <span>{{ game.first_team }}</span>
                </div>
                <span class="featured-vs">vs</span>
                <div class="team-stack">
                  <TeamLogoBadge :src="game.second_team_logo" :team-name="game.second_team" :size="46" />
                  <span>{{ game.second_team }}</span>
                </div>
              </div>

              <div class="featured-pick">
                <span class="pick-label">Top Pick</span>
                <strong>{{ game.pro_category }}</strong>
              </div>

              <div class="featured-bottom">
                <span class="metric-pill">
                  <small>Confidence</small>
                  <strong>{{ formatProbability(game.pro_prob) }}</strong>
                </span>
                <span class="metric-pill">
                  <small>Odds</small>
                  <strong>{{ formatOdds(game.pro_odds) }}</strong>
                </span>
              </div>
            </article>
          </div>
        </section>

        <section class="league-section">
          <div class="section-heading">
            <h2>Upcoming Matches</h2>
            <p>Browse every upcoming fixture in this league.</p>
          </div>

          <div class="upcoming-grid">
            <article
              v-for="game in leagueHub.upcoming"
              :key="game.id"
              class="upcoming-card"
              @click="openGame(game.id)"
            >
              <div class="upcoming-top">
                <span class="upcoming-kickoff">{{ formatDateTime(game.date) }}</span>
                <span class="upcoming-odds">Odds {{ formatOdds(game.pro_odds) }}</span>
              </div>

              <div class="upcoming-body">
                <div class="team-column">
                  <TeamLogoBadge :src="game.first_team_logo" :team-name="game.first_team" :size="34" />
                  <span>{{ game.first_team }}</span>
                </div>

                <div class="upcoming-center">
                  <span class="upcoming-vs">vs</span>
                  <span class="upcoming-category">{{ game.pro_category }}</span>
                </div>

                <div class="team-column">
                  <TeamLogoBadge :src="game.second_team_logo" :team-name="game.second_team" :size="34" />
                  <span>{{ game.second_team }}</span>
                </div>
              </div>

              <div class="upcoming-bottom">
                <span class="metric-pill metric-pill--soft">
                  <small>Confidence</small>
                  <strong>{{ formatProbability(game.pro_prob) }}</strong>
                </span>
              </div>
            </article>
          </div>
        </section>

        <section class="league-section">
          <div class="section-heading">
            <h2>Standings</h2>
            <p>{{ standingsDescription }}</p>
          </div>

          <div class="standings-table-wrap">
            <table class="standings-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Team</th>
                  <th>P</th>
                  <th>W</th>
                  <th>D</th>
                  <th>L</th>
                  <th>GD</th>
                  <th>Pts</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="team in leagueHub.standings" :key="team.team_id">
                  <td>{{ team.position }}</td>
                  <td>{{ team.team_name }}</td>
                  <td>{{ team.played }}</td>
                  <td>{{ team.wins }}</td>
                  <td>{{ team.draws }}</td>
                  <td>{{ team.losses }}</td>
                  <td>{{ team.goal_difference }}</td>
                  <td>{{ team.points }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </section>

      <section v-else class="league-empty">
        <WelcomeText />
        <p>We could not find an upcoming league board for this page yet.</p>
      </section>
    </main>

    <AppFooter />
  </div>
</template>

<script>
import AppFooter from './AppFooter.vue';
import AppHeader from './AppHeader.vue';
import CardRailSkeleton from './CardRailSkeleton.vue';
import TeamLogoBadge from './TeamLogoBadge.vue';
import WelcomeText from './WelcomeText.vue';
import { fetchJsonWithCache } from '../services/apiCache';
import { formatLeagueName } from '../utils/formatLeagueName';

const SITE_URL = 'https://www.balleralgo.com';

export default {
  name: 'LeaguePage',
  components: {
    AppFooter,
    AppHeader,
    CardRailSkeleton,
    TeamLogoBadge,
    WelcomeText,
  },
  props: {
    leagueSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      leagueHub: null,
      isLoading: true,
    };
  },
  computed: {
    displayLeagueName() {
      return this.leagueHub?.league?.name || formatLeagueName(this.leagueHub?.league?.key);
    },
    featuredGames() {
      if (!this.leagueHub) {
        return [];
      }

      const games = [];
      const seenIds = new Set();
      const appendGame = (game) => {
        if (!game || seenIds.has(game.id)) {
          return;
        }
        seenIds.add(game.id);
        games.push(game);
      };

      (this.leagueHub.featured || []).forEach(appendGame);

      if (games.length < 3) {
        [...(this.leagueHub.upcoming || [])]
          .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
          .forEach(appendGame);
      }

      return games.slice(0, 3);
    },
    standingsDescription() {
      if (!this.leagueHub?.standings?.length) {
        return 'League table preview.';
      }

      const hasPlayedGames = this.leagueHub.standings.some((team) => Number(team.played || 0) > 0);
      return hasPlayedGames
        ? 'Current table built from completed matches in the selected season.'
        : 'Preview table for this league season. Stats will fill in as completed scores are synced.';
    },
  },
  watch: {
    leagueSlug: {
      immediate: true,
      handler() {
        this.fetchLeagueHub();
      },
    },
  },
  methods: {
    async fetchLeagueHub() {
      this.isLoading = true;
      this.leagueHub = null;
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/football/league/${this.leagueSlug}`;
        this.leagueHub = await fetchJsonWithCache({
          key: `football:league:${this.leagueSlug}`,
          url: apiUrl,
          ttlMs: 15000,
        });
        this.applyLeagueSeo();
      } catch (error) {
        console.error('Failed to fetch league hub:', error);
        this.leagueHub = null;
        this.applyLeagueNoindex();
      } finally {
        this.isLoading = false;
      }
    },
    formatProbability(value) {
      return `${(Number(value || 0) * 100).toFixed(1)}%`;
    },
    formatOdds(value) {
      return value !== undefined && value !== null ? Number(value).toFixed(2) : 'N/A';
    },
    formatDateTime(value) {
      const date = new Date(value);
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    kickoffLabel(value) {
      const date = new Date(value);
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
      });
    },
    openGame(gameId) {
      if (!gameId) {
        return;
      }
      this.$router.push(`/football/${gameId}`);
    },
    applyLeagueSeo() {
      if (!this.leagueHub?.league) {
        return;
      }

      const leagueName = this.displayLeagueName;
      const season = this.leagueHub.league.season || 'current season';
      const upcomingCount = Number(this.leagueHub.league.upcoming_matches || 0);
      const title = `${leagueName} Predictions, Fixtures & Standings | BallerAlgo`;
      const description = `Browse ${leagueName} predictions, standings, featured matches, and ${upcomingCount} upcoming fixtures for the ${season} season on BallerAlgo.`;
      const canonicalUrl = `${SITE_URL}/football/league/${this.leagueSlug}`;

      document.title = title;

      Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach((el) => el.parentNode.removeChild(el));

      const headEntries = [
        { tag: 'link', attrs: { rel: 'canonical', href: canonicalUrl } },
        { tag: 'meta', attrs: { name: 'description', content: description } },
        { tag: 'meta', attrs: { property: 'og:title', content: title } },
        { tag: 'meta', attrs: { property: 'og:description', content: description } },
        { tag: 'meta', attrs: { property: 'og:type', content: 'website' } },
        { tag: 'meta', attrs: { property: 'og:url', content: canonicalUrl } },
        { tag: 'meta', attrs: { property: 'og:site_name', content: 'BallerAlgo' } },
        { tag: 'meta', attrs: { property: 'og:locale', content: 'en_US' } },
        { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
        { tag: 'meta', attrs: { name: 'twitter:title', content: title } },
        { tag: 'meta', attrs: { name: 'twitter:description', content: description } },
        { tag: 'meta', attrs: { name: 'robots', content: 'index, follow' } },
      ];

      headEntries.forEach(({ tag, attrs }) => {
        const element = document.createElement(tag);
        Object.entries(attrs).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
        element.setAttribute('data-vue-router-controlled', '');
        document.head.appendChild(element);
      });

      const structuredData = [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${leagueName} Predictions, Fixtures & Standings`,
          description,
          url: canonicalUrl,
          isPartOf: {
            '@type': 'WebSite',
            name: 'BallerAlgo',
            url: SITE_URL,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: SITE_URL,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Football',
              item: `${SITE_URL}/football`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: leagueName,
              item: canonicalUrl,
            },
          ],
        },
      ];

      structuredData.forEach((payload) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-vue-router-controlled', '');
        script.textContent = JSON.stringify(payload);
        document.head.appendChild(script);
      });
    },
    applyLeagueNoindex() {
      const canonicalUrl = `${SITE_URL}/football/league/${this.leagueSlug}`;
      const title = 'League Page Not Available | BallerAlgo';
      const description = 'This league page does not currently have enough upcoming data to be indexed on BallerAlgo.';

      document.title = title;
      Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach((el) => el.parentNode.removeChild(el));

      const headEntries = [
        { tag: 'link', attrs: { rel: 'canonical', href: canonicalUrl } },
        { tag: 'meta', attrs: { name: 'description', content: description } },
        { tag: 'meta', attrs: { name: 'robots', content: 'noindex, follow' } },
      ];

      headEntries.forEach(({ tag, attrs }) => {
        const element = document.createElement(tag);
        Object.entries(attrs).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
        element.setAttribute('data-vue-router-controlled', '');
        document.head.appendChild(element);
      });
    },
  },
};
</script>

<style scoped>
.league-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #ffffff;
}

.league-content {
  flex: 1;
  padding: 16px 18px 32px;
}

.league-shell {
  width: min(1180px, 100%);
  margin: 0 auto;
}

.league-hero {
  padding: 20px 6px 6px;
}

.sport-label {
  margin: 0 0 8px;
  color: #7d8a97;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.league-hero h1 {
  margin: 0;
  color: #173252;
  font-size: clamp(2rem, 4vw, 3rem);
}

.hero-description {
  margin: 10px 0 0;
  color: #5c6976;
}

.league-intro {
  margin-top: 12px;
  padding: 18px 20px;
  border: 1px solid #e2eaf2;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.league-intro p {
  margin: 0;
  color: #5d7083;
  line-height: 1.7;
}

.league-intro-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.league-intro-links a {
  padding: 9px 14px;
  border: 1px solid #d7e3ef;
  border-radius: 999px;
  background: #ffffff;
  color: #173252;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.league-intro-links a:hover {
  background: #eef5fb;
  border-color: #bfd1e1;
}

.league-section {
  margin-top: 26px;
}

.section-heading {
  margin-bottom: 14px;
}

.section-heading h2 {
  margin: 0 0 4px;
  color: #173252;
}

.section-heading p {
  margin: 0;
  color: #6a7682;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.featured-card,
.upcoming-card {
  border: 1px solid #dfe8ef;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(21, 41, 68, 0.06);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.featured-card:hover,
.upcoming-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px rgba(21, 41, 68, 0.1);
}

.featured-card {
  padding: 16px;
}

.featured-top {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
}

.featured-tag,
.upcoming-kickoff,
.upcoming-odds {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
}

.featured-tag,
.upcoming-kickoff {
  background: #eef4fa;
  color: #1d4f7b;
}

.upcoming-odds {
  background: #edf8f1;
  color: #2f6447;
}

.featured-matchup {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.featured-vs,
.upcoming-vs {
  color: #7d8a97;
  font-weight: 700;
}

.team-stack,
.team-column {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.team-stack {
  flex-direction: column;
  text-align: center;
}

.team-stack span,
.team-column span {
  color: #173252;
  font-weight: 700;
}

.featured-pick {
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbfe 0%, #eef5fb 100%);
  margin-bottom: 14px;
}

.pick-label,
.metric-pill small {
  display: block;
  color: #6a7682;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.featured-pick strong {
  display: block;
  margin-top: 6px;
  color: #173252;
  font-size: 1.02rem;
}

.featured-bottom,
.upcoming-bottom {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.metric-pill {
  min-width: 110px;
  padding: 10px 12px;
  border-radius: 16px;
  background: #f6f9fc;
  color: #173252;
}

.metric-pill--soft {
  background: #edf8f1;
}

.metric-pill strong {
  display: block;
  margin-top: 4px;
  font-size: 1rem;
}

.upcoming-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.upcoming-card {
  padding: 16px;
}

.upcoming-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.upcoming-body {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.team-column {
  flex-direction: column;
  text-align: center;
}

.upcoming-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upcoming-category {
  padding: 8px 12px;
  border-radius: 999px;
  background: #f5f8fb;
  color: #173252;
  font-size: 0.84rem;
  font-weight: 700;
  text-align: center;
}

.standings-table-wrap {
  overflow-x: auto;
  border: 1px solid #dfe8ef;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(21, 41, 68, 0.04);
}

.standings-table {
  width: 100%;
  border-collapse: collapse;
}

.standings-table th,
.standings-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #edf1f5;
  color: #173252;
}

.standings-table th {
  font-size: 0.78rem;
  color: #6a7682;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.league-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 30px 18px;
}

@media (max-width: 1080px) {
  .featured-grid,
  .upcoming-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .league-content {
    padding: 12px 12px 24px;
  }

  .league-hero {
    padding: 16px 2px 2px;
  }

  .league-intro {
    padding: 16px;
    border-radius: 18px;
  }

  .featured-matchup,
  .upcoming-body {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 10px;
  }

  .team-stack,
  .team-column {
    gap: 8px;
  }

  .team-stack span,
  .team-column span {
    font-size: 0.9rem;
  }

  .upcoming-vs {
    order: 0;
  }

  .upcoming-category {
    font-size: 0.78rem;
    padding: 7px 10px;
  }

  .upcoming-top,
  .upcoming-bottom {
    flex-direction: column;
    align-items: stretch;
  }

  .featured-bottom {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: nowrap;
  }

  .metric-pill {
    min-width: 0;
    flex: 1;
  }
}
</style>
